import React from 'react';
import type { RouteChildrenProps } from 'react-router';
import { Redirect, Route, Switch } from 'react-router';

import { config } from 'src/config';
import { Layout } from 'src/modules/app/components/Layout';
import { RedirectWithSession } from 'src/modules/app/components/RedirectWithSession';
import { BulkCaptureInvite } from 'src/modules/bulkCaptureInvite';
import { DeisignOrders } from 'src/modules/designOrders';
import { DesignStudio } from 'src/modules/designStudio';
import { HomeownerApp } from 'src/modules/homeownerApp';
import { EmbedContainer } from 'src/modules/homeownerApp/components/Embed/components/EmbedContainer';
import { ProjectShare } from 'src/modules/homeownerApp/components/ProjectShare';
import { Internal } from 'src/modules/internal';
import { Login } from 'src/modules/login';
import { ManageTeams } from 'src/modules/manageTeams';
import { NewConstructionUpload } from 'src/modules/newConstructionUpload';
import { NotFound } from 'src/modules/notFound';
import { Onboarding } from 'src/modules/onboarding';
import { useOnboardingSplit } from 'src/modules/onboarding/hooks/useOnboardingSplit';
import { PaintMeasurements } from 'src/modules/paintMeasurements';
import { ProPage } from 'src/modules/pro/components/ProPage';
import { Properties } from 'src/modules/properties';
import { PropertyDetails } from 'src/modules/propertyDetails';
import { Renderings } from 'src/modules/renderings';
import { Activate } from 'src/modules/renderings/components/Activate';
import { Layout as RenderingsLayout } from 'src/modules/renderings/components/Layout';
import { SignUp } from 'src/modules/signUp';
import { Mobile } from 'src/modules/signUp/components/Mobile';
import { SomethingWrong } from 'src/modules/somethingWrong';
import { VirtualWalkthrough } from 'src/modules/virtualWalkthrough';
import { WebUpload } from 'src/modules/webUpload';
import { SkipSSR } from 'src/shared/components/SSR/SkipSSR';
import { useLocation } from 'src/shared/hooks';
import { AuthenticatedLayout } from 'src/shared/layouts/AuthenticatedLayout/AuthenticatedLayout';
import { AuthenticatedLayoutWithNavigation } from 'src/shared/layouts/AuthenticatedLayout/AuthenticatedLayoutWithNavigation';
import { Provider as ToastProvider } from 'src/shared/providers/Toaster';
import { HOMEOWNER_APP_ROOT_ROUTE, Route as Routes } from 'src/shared/route';

import { ProjectRoute } from './ProjectRoute';

const OnboardingExperimentRouter = ({ location }: RouteChildrenProps) => {
  const { search } = location;
  const { showOnboardingLogin, showReskinnedSignup, isLoading } =
    useOnboardingSplit();

  if (isLoading) {
    return null;
  }

  const oldLogin = (
    <ProjectRoute
      layoutWrapper={Layout}
      path={[Routes.LOGIN.path, Routes.SIGN_UP.path]}
    >
      <Route path={Routes.SIGNUP_MOBILE.path}>
        <Mobile />
      </Route>
      <Route path={Routes.LOGIN.path}>
        <Login />
      </Route>
      <Route path={Routes.SIGN_UP.path}>
        <SignUp />
      </Route>
    </ProjectRoute>
  );

  // All new reskinned routes should be enabled
  if (showReskinnedSignup) {
    return (
      <Switch>
        <Redirect
          exact
          from={Routes.LOGIN.path}
          to={`${Routes.SIGN_UP_ONBOARDING.path}/welcome${search}`}
        />
        <Redirect
          exact
          from={Routes.LOGIN_FORGOT.path}
          to={`${Routes.SIGN_UP_ONBOARDING.path}/reset`}
        />
        <Redirect
          exact
          from={Routes.SIGN_UP.path}
          to={`${Routes.SIGN_UP_ONBOARDING.path}/start${search}`}
        />
        <Redirect
          exact
          from={Routes.SIGN_UP_HOMEOWNER.path}
          to={`${Routes.SIGN_UP_ONBOARDING.path}/homeowner${search}`}
        />
        <Redirect
          exact
          from={Routes.SIGN_UP_PROFESSIONAL.path}
          to={`${Routes.SIGN_UP_ONBOARDING.path}/pro${search}`}
        />
        <Redirect
          exact
          from={Routes.SIGN_UP_INSURANCE.path}
          to={`${Routes.SIGN_UP_ONBOARDING.path}/insurance${search}`}
        />
        <Redirect
          exact
          from={`${Routes.SIGN_UP_ONBOARDING.path}/signup/link${search}`}
          to={`${Routes.SIGN_UP_ONBOARDING.path}/start${search}`}
        />
        <Redirect
          from={Routes.LOGIN_SSO_SUCCESS.path}
          to={`${Routes.SIGN_UP_ONBOARDING.path}/ssoSuccess${search}`}
        />
        <Redirect
          from={Routes.LOGIN_SSO_FAILURE.path}
          to={`${Routes.SIGN_UP_ONBOARDING.path}/ssoFailure${search}`}
        />
        <ProjectRoute path={Routes.SIGN_UP_ONBOARDING_STEP.path}>
          <Onboarding />
        </ProjectRoute>
        {oldLogin}
      </Switch>
    );
  }

  // Only login, not sign up available
  if (showOnboardingLogin) {
    return (
      <Switch>
        <Redirect
          exact
          from={Routes.LOGIN.path}
          to={`${Routes.SIGN_UP_ONBOARDING.path}/welcome${search}`}
        />
        <Redirect
          exact
          from={Routes.LOGIN_FORGOT.path}
          to={`${Routes.SIGN_UP_ONBOARDING.path}/reset`}
        />
        <Redirect
          from={Routes.LOGIN_SSO_SUCCESS.path}
          to={`${Routes.SIGN_UP_ONBOARDING.path}/ssoSuccess${search}`}
        />
        <Redirect
          from={Routes.LOGIN_SSO_FAILURE.path}
          to={`${Routes.SIGN_UP_ONBOARDING.path}/ssoFailure${search}`}
        />
        <Redirect
          exact
          from={`${Routes.SIGN_UP_ONBOARDING.path}/signup`}
          to={Routes.SIGN_UP.path + search}
        />
        <Redirect
          exact
          from={`${Routes.SIGN_UP_ONBOARDING.path}/create`}
          to={Routes.SIGN_UP.path}
        />
        <ProjectRoute path={Routes.SIGN_UP_ONBOARDING_STEP.path}>
          <Onboarding />
        </ProjectRoute>
        {oldLogin}
      </Switch>
    );
  }

  return (
    <Switch>
      <Redirect
        from={`${Routes.SIGN_UP_ONBOARDING.path}/welcome${search}`}
        to={`${Routes.LOGIN.path}${search}`}
      />
      <Redirect
        exact
        from={`${Routes.SIGN_UP_ONBOARDING.path}/start`}
        to={`${Routes.SIGN_UP.path}${search}`}
      />
      <Redirect
        exact
        from={`${Routes.SIGN_UP_ONBOARDING.path}/homeowner`}
        to={`${Routes.SIGN_UP_HOMEOWNER.path}${search}`}
      />
      <Redirect
        exact
        from={`${Routes.SIGN_UP_ONBOARDING.path}/pro`}
        to={`${Routes.SIGN_UP_PROFESSIONAL.path}${search}`}
      />
      <Redirect
        exact
        from={`${Routes.SIGN_UP_ONBOARDING.path}/insurance`}
        to={`${Routes.SIGN_UP_INSURANCE.path}${search}`}
      />
      <Redirect
        exact
        from={`${Routes.SIGN_UP_ONBOARDING.path}/reset`}
        to={Routes.LOGIN_FORGOT.path}
      />
      <Redirect
        exact
        from={`${Routes.SIGN_UP_ONBOARDING.path}/create`}
        to={Routes.SIGN_UP.path}
      />
      <Redirect
        exact
        from={`${Routes.SIGN_UP_ONBOARDING.path}/signup/link${search}`}
        to={`${Routes.SIGN_UP_HOMEOWNER.path}${search}`}
      />
      <Redirect
        from={`${Routes.SIGN_UP_ONBOARDING.path}/ssoSuccess`}
        to={`${Routes.LOGIN_SSO_SUCCESS.path}${search}`}
      />
      <Redirect
        from={`${Routes.SIGN_UP_ONBOARDING.path}/ssoFailure`}
        to={`${Routes.LOGIN_SSO_FAILURE.path}${search}`}
      />
      {/* This is a catch-all, all redirects should be above this */}
      <Redirect
        from={Routes.SIGN_UP_ONBOARDING.path}
        to={`${Routes.LOGIN.path}${search}`}
      />
      {oldLogin}
    </Switch>
  );
};

const Router = () => (
  <Switch>
    {/* Design Orders paths */}
    <ProjectRoute
      dsn={config.SENTRY_RENDERING_HUB_DSN}
      path={[
        Routes.DESIGN_ORDERS_HOMEOWNER_REQUEST.path,
        Routes.DESIGN_ORDERS_HOMEOWNER_EDIT.path,
        Routes.DESIGN_ORDERS_PRO_REQUEST.path,
        Routes.DESIGN_ORDERS_PRO_EDIT.path,
        Routes.DESIGN_ORDERS_SUMMARY.path,
      ]}
    >
      <DeisignOrders />
    </ProjectRoute>
    {/* Design Studio paths */}
    <ProjectRoute
      dsn={config.SENTRY_DESIGN_STUDIO_DSN}
      layoutWrapper={ToastProvider}
      path={Routes.DESIGN_STUDIO.path}
    >
      <Route path={Routes.DESIGN_STUDIO.path}>
        <DesignStudio />
      </Route>
    </ProjectRoute>
    <ProjectRoute
      dsn={config.SENTRY_RENDERING_HUB_DSN}
      layoutWrapper={ToastProvider}
      path={Routes.RENDERINGS_INTERACTIVE.path}
    >
      <DesignStudio />
    </ProjectRoute>
    <ProjectRoute
      dsn={config.SENTRY_RENDERING_HUB_DSN}
      layoutWrapper={RenderingsLayout}
      path={[Routes.RENDERINGS_SHARED.path, Routes.RENDERINGS_HUB.path]}
    >
      <Renderings />
    </ProjectRoute>
    <ProjectRoute
      dsn={config.SENTRY_HOMEOWNER_APP_DSN}
      path={Routes.PROJECT_SHARE.path}
    >
      <ProjectShare />
    </ProjectRoute>
    <Route path={Routes.PRO.path}>
      <SkipSSR>
        <ProPage />
      </SkipSSR>
    </Route>

    <Route path={Routes.EMBED.path}>
      <EmbedContainer />
    </Route>

    <ProjectRoute
      dsn={config.SENTRY_HOMEOWNER_APP_DSN}
      path={[
        HOMEOWNER_APP_ROOT_ROUTE,
        Routes.AI_DESIGNER.path,
        Routes.AI_DESIGNER_INSPIRATIONS.path,
        Routes.AI_DESIGNER_SHARE.path,
      ]}
    >
      <HomeownerApp />
    </ProjectRoute>
    <ProjectRoute path={[Routes.BULK_CAPTURE_INVITE.path]}>
      <BulkCaptureInvite />
    </ProjectRoute>
    <ProjectRoute
      layoutWrapper={AuthenticatedLayoutWithNavigation}
      path={[Routes.PROPERTY_DETAILS.path]}
    >
      <PropertyDetails />
    </ProjectRoute>
    <ProjectRoute
      layoutWrapper={AuthenticatedLayoutWithNavigation}
      path={[Routes.PROPERTIES.path]}
    >
      <SkipSSR>
        <Properties />
      </SkipSSR>
    </ProjectRoute>
    <ProjectRoute
      layoutWrapper={AuthenticatedLayoutWithNavigation}
      path={[Routes.GROUPS.path]}
    >
      <SkipSSR>
        <Properties />
      </SkipSSR>
    </ProjectRoute>
    <ProjectRoute path={Routes.WEB_UPLOAD.path}>
      <WebUpload />
    </ProjectRoute>

    <ProjectRoute
      layoutWrapper={AuthenticatedLayoutWithNavigation}
      path={[Routes.MANAGE_TEAMS.path]}
    >
      <SkipSSR>
        <ManageTeams />
      </SkipSSR>
    </ProjectRoute>

    <ProjectRoute path={[Routes.NEW_CONSTRUCTION_UPLOAD.path]}>
      <SkipSSR>
        <NewConstructionUpload />
      </SkipSSR>
    </ProjectRoute>
    <ProjectRoute path={[Routes.PAINT_MEASUREMENTS_SHARED.path]}>
      <PaintMeasurements />
    </ProjectRoute>
    <ProjectRoute
      layoutWrapper={AuthenticatedLayoutWithNavigation}
      path={[Routes.PAINT_MEASUREMENTS.path]}
    >
      <PaintMeasurements />
    </ProjectRoute>
    <Route
      component={OnboardingExperimentRouter}
      path={[
        Routes.LOGIN.path,
        Routes.SIGN_UP_ONBOARDING_STEP.path,
        Routes.SIGN_UP.path,
      ]}
    />
    <ProjectRoute
      dsn={config.VIRTUAL_WALKTHROUGH_DSN}
      layoutWrapper={AuthenticatedLayout}
      path={[Routes.VIRTUAL_WALKTHROUGH.path]}
    >
      <SkipSSR>
        <VirtualWalkthrough />
      </SkipSSR>
    </ProjectRoute>
    <ProjectRoute layoutWrapper={Layout}>
      <Route path={Routes.RENDERINGS_USER_ACTIVATE.path}>
        <RedirectWithSession>
          <Activate />
        </RedirectWithSession>
      </Route>
      {/* Ideally we could get Beacon to update this after we've migrated away
      from the WordPress-based `/login` and the `/beacon_return` in `website`,
      but I'm not sure if it's worth the trouble */}
      <Redirect
        from="/beacon_return"
        to={Routes.LOGIN_BEACON_SUCCESS.path + useLocation().search}
      />
      <Route path={Routes.INTERNAL.path}>
        <Internal />
      </Route>
      <Route exact path={Routes.ROOT.path}>
        <Redirect to={Routes.LOGIN.path} />
      </Route>
      {/* Banned devices */}
      <Route path={Routes.SOMETHING_WRONG.path}>
        <SomethingWrong />
      </Route>
      <Route path="*">
        <NotFound />
      </Route>
    </ProjectRoute>
  </Switch>
);

export { Router };
